import React from 'react'
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts'
import { ExpandIcon, ContractIcon } from './Icons'
import { CustomTooltip } from './Tooltip'
import { formatNumber } from '../../../utils'

const EconomicStrengthSection = ({
  safeSelectedYear,
  safeYears,
  safeIncomeAndPopData,
  showIncomePolygonData,
  polygonData,
  polygonColors,
  selectedIncomePoint,
  safeTargetGroupName,
  singlePolygonData,
  singlePolygonId,
  rightSidebarStore,
  uiStore,
  isExpanded,
  onToggleExpand
}) => {
  const [tooltipConfig, setTooltipConfig] = React.useState({
    active: false,
    payload: [],
    coordinate: { x: 0, y: 0 },
    position: 'left' // 'left', 'right', 'top', 'bottom'
  })
  const chartContainerRef = React.useRef(null)
  const unhighlightTimerRef = React.useRef(null)
  const lastHighlightedIdRef = React.useRef(null)
  const viewSwitchBlockRef = React.useRef(false)
  const tooltipHoverRef = React.useRef(false)
  const hideTooltipTimerRef = React.useRef(null)
  const [timeoutIDs, setTimeoutIDs] = React.useState([])

  const clearUnhighlightTimer = () => {
    if (unhighlightTimerRef.current) {
      clearTimeout(unhighlightTimerRef.current)
      unhighlightTimerRef.current = null
    }
  }

  const clearHideTooltipTimer = () => {
    if (hideTooltipTimerRef.current) {
      clearTimeout(hideTooltipTimerRef.current)
      hideTooltipTimerRef.current = null
    }
  }

  // Reset tooltip and highlights when view mode changes
  React.useEffect(() => {
    // Block new tooltips temporarily to prevent "sticky" immediate reopening
    viewSwitchBlockRef.current = true
    setTimeout(() => {
      viewSwitchBlockRef.current = false
    }, 500)

    // 1. Hide tooltip
    setTooltipConfig(prev => ({ ...prev, active: false }))

    // 2. Clear any active highlight
    clearUnhighlightTimer()
    if (lastHighlightedIdRef.current && uiStore) {
      uiStore.unhighlightPolygonFromChart(lastHighlightedIdRef.current)
      lastHighlightedIdRef.current = null
    }
  }, [showIncomePolygonData, singlePolygonData, safeIncomeAndPopData])

  const handleMouseEnter = (data, e) => {
    // If we are in the cooldown period after a view switch, ignore hover
    if (viewSwitchBlockRef.current) return

    // Clear any pending hide timers when entering a dot
    clearUnhighlightTimer()
    clearHideTooltipTimer()

    // Set tooltip data
    if (data) {
      // Calculate optimal tooltip position based on chart boundaries
      const chartContainer = chartContainerRef.current
      const chartWidth = chartContainer ? chartContainer.clientWidth : 360
      const chartHeight = 300
      const tooltipWidth = 140
      const tooltipHeight = 100 // Approximate height
      const margin = { top: 20, right: 20, bottom: 20, left: 20 }

      let position = 'left' // default

      // Check horizontal positioning
      const isNearLeftEdge = data.cx - tooltipWidth < margin.left
      const isNearRightEdge = data.cx + tooltipWidth > chartWidth - margin.right

      // Check vertical positioning
      const isNearTopEdge = data.cy - tooltipHeight < margin.top
      const isNearBottomEdge = data.cy + tooltipHeight > chartHeight - margin.bottom

      // Determine position priority: check if near edges
      if (isNearLeftEdge && !isNearRightEdge) {
        position = 'right' // Show on right if near left edge
      } else if (isNearRightEdge) {
        position = 'left' // Show on left if near right edge
      } else if (isNearBottomEdge && !isNearTopEdge) {
        position = 'top' // Show above if near bottom edge
      } else if (isNearTopEdge) {
        position = 'bottom' // Show below if near top edge
      } else {
        position = 'left' // Default to left
      }

      const timeouts = timeoutIDs
      timeouts.forEach(id => clearTimeout(id))
      setTimeoutIDs([])

      setTooltipConfig({
        active: true,
        payload: [{ payload: data }],
        coordinate: { x: data.cx, y: data.cy },
        position
      })

      // Handle highlighting
      const polygonId = singlePolygonData ? data.id : (data.id === 0 ? null : data.id)

      if (typeof polygonId === 'number' && polygonId !== 0 && uiStore) {
        // If there's an existing highlight that's different, clear it first
        if (lastHighlightedIdRef.current && lastHighlightedIdRef.current !== polygonId) {
          uiStore.unhighlightPolygonFromChart(lastHighlightedIdRef.current)
        }

        // Highlight new one and store ref
        uiStore.highlightPolygonFromChart(polygonId, data.color)
        lastHighlightedIdRef.current = polygonId
      }
    }
  }

  const handleMouseLeave = (data) => {
    const timeouts = timeoutIDs

    timeouts.push(setTimeout(() => {
      // Only hide tooltip if not hovering over it
      setTooltipConfig(prev => ({ ...prev, active: false }))
    }, 1200)) // Delay to allow moving to tooltip

    setTimeoutIDs(timeouts)

    // Immediately clear timers
    // clearUnhighlightTimer()
    // clearHideTooltipTimer()

    // // Set flag to false immediately when leaving dot
    // const wasHoveringTooltip = tooltipHoverRef.current

    // // If not hovering tooltip, hide immediately with delay for bridge
    // if (!wasHoveringTooltip) {
    //   hideTooltipTimerRef.current = setTimeout(() => {
    //     if (!tooltipHoverRef.current) {
    //       setTooltipConfig(prev => ({ ...prev, active: false }))
    //       const currentId = lastHighlightedIdRef.current
    //       if (currentId && uiStore) {
    //         uiStore.unhighlightPolygonFromChart(currentId)
    //         lastHighlightedIdRef.current = null
    //       }
    //     }
    //   }, 300) // 300ms delay to allow moving to tooltip bridge

    // Handle unhighlighting
    unhighlightTimerRef.current = setTimeout(() => {
      if (!tooltipHoverRef.current) {
        const currentId = lastHighlightedIdRef.current
        if (currentId && uiStore) {
          uiStore.unhighlightPolygonFromChart(currentId)
          lastHighlightedIdRef.current = null
        }
      }
    }, 50)
    // }
  }

  const handleTooltipEnter = () => {
    const timeouts = timeoutIDs
    timeouts.forEach(timeout => clearTimeout(timeout))
    setTimeoutIDs([])

    tooltipHoverRef.current = true
    clearHideTooltipTimer()
    clearUnhighlightTimer()
  }

  const handleTooltipLeave = () => {
    tooltipHoverRef.current = false
    setTooltipConfig(prev => ({ ...prev, active: false }))
    clearHideTooltipTimer()
    clearUnhighlightTimer()
    if (lastHighlightedIdRef.current && uiStore) {
      uiStore.unhighlightPolygonFromChart(lastHighlightedIdRef.current)
      lastHighlightedIdRef.current = null
    }
  }

  const handleChartMouseLeave = () => {
    // Hide tooltip when mouse leaves chart area (not tooltip)
    if (!tooltipHoverRef.current) {
      setTooltipConfig(prev => ({ ...prev, active: false }))
      clearHideTooltipTimer()
      clearUnhighlightTimer()
      if (lastHighlightedIdRef.current && uiStore) {
        uiStore.unhighlightPolygonFromChart(lastHighlightedIdRef.current)
        lastHighlightedIdRef.current = null
      }
    }
  }

  return (
    <div className={`income-data-sidebar__section ${isExpanded ? 'income-data-sidebar__section--expanded-chart' : ''}`}>
      <div className='income-data-sidebar__section-header'>
        <h3 className='income-data-sidebar__section-title'>Economic Strength</h3>
        <div className='income-data-sidebar__section-actions'>
          <div className='income-data-sidebar__dropdown'>
            <select
              className='income-data-sidebar__dropdown-select'
              value={safeSelectedYear}
              onChange={(e) => rightSidebarStore.setIncomeSelectedYear(e.target.value)}
            >
              {safeYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            {/* <DropdownIcon /> */}
          </div>
          <button
            type='button'
            className='income-data-sidebar__expand-btn'
            onClick={onToggleExpand}
            aria-label={isExpanded ? 'Contract economic strength chart' : 'Expand economic strength chart'}
          >
            {isExpanded ? <ContractIcon /> : <ExpandIcon />}
          </button>
        </div>
      </div>
      <div
        ref={chartContainerRef}
        className='income-data-sidebar__chart-container income-data-sidebar__chart-container--visible'
      >
        {safeIncomeAndPopData.length > 0
          ? (
            <ResponsiveContainer width='100%' height={isExpanded ? '100%' : 300} style={{ overflow: 'visible' }}>
              <ScatterChart
                margin={{ top: 20, right: 20, bottom: 45, left: 20 }}
                onClick={(e) => {
                  // If clicking on chart background (not on a point), deselect or toggle
                  if (e && e.target && e.target.tagName !== 'circle') {
                    if (showIncomePolygonData) {
                      // Hide both income and people polygon views
                      rightSidebarStore.setShowIncomePolygonData(false)
                      rightSidebarStore.showPolygonLines = false
                      rightSidebarStore.showIndividualPolygonLines = false
                    }
                  }
                }}
                onMouseLeave={handleChartMouseLeave}
              >
                <CartesianGrid strokeDasharray='3 3' stroke='#E5E7EB' />
                <XAxis
                  type='number'
                  dataKey='population'
                  name='Population'
                  stroke='#6B7280'
                  fontSize={10}
                  domain={[dataMin => dataMin / 2, dataMax => (dataMax * 1.1)]}
                  tickLine={false}
                  label={{ value: 'Population', position: 'bottom', offset: 25 }}
                  tickFormatter={(value) => {
                    if (value >= 1000) return formatNumber(value)
                    return value.toString()
                  }}
                />
                <YAxis
                  type='number'
                  dataKey='income'
                  name='Income'
                  stroke='#6B7280'
                  fontSize={10}
                  tickLine={false}
                  domain={[dataMin => dataMin / 2, dataMax => (dataMax * 1.1)]}
                  label={{ value: 'HH income', angle: -90, position: 'insideLeft', offset: 0 }}
                  tickFormatter={(value) => {
                    if (value >= 1000) return `$${formatNumber(value)}`
                    return `$${formatNumber(value)}`
                  }}
                />
                {tooltipConfig.active && (
                  <Tooltip
                    content={
                      <CustomTooltip
                        active={tooltipConfig.active}
                        showIncomePolygonData={showIncomePolygonData}
                        singlePolygonData={singlePolygonData}
                        rightSidebarStore={rightSidebarStore}
                        uiStore={uiStore}
                        onKeepHighlight={clearUnhighlightTimer}
                        onDropHighlight={handleMouseLeave}
                        onTooltipEnter={handleTooltipEnter}
                        onTooltipLeave={handleTooltipLeave}
                        position={tooltipConfig.position}
                      />
                    }
                    cursor={{ strokeDasharray: '3 3' }}
                    isAnimationActive={false}
                    allowEscapeViewBox={{ x: true, y: true }}
                    offset={0}
                    wrapperStyle={{ zIndex: 1000, pointerEvents: 'auto' }}
                    active
                    trigger='click'
                    payload={tooltipConfig.payload}
                    coordinate={tooltipConfig.coordinate}
                  />
                )}
                {safeIncomeAndPopData.map((dataPoint, index) => {
                  const color = dataPoint.color || polygonColors[index % polygonColors.length]
                  const isSelected = selectedIncomePoint === dataPoint.id

                  return (
                    <Scatter
                      key={dataPoint.id || index}
                      name={dataPoint.name || 'Data Point'}
                      data={[dataPoint]}
                      fill={color}
                      shape='circle'
                      onMouseEnter={(data) => {
                        // Recharts passes the element props as 'data', which includes payload, cx, cy etc.
                        // The actual data point is in data.payload or we can use the 'dataPoint' from closure
                        // BUT for cx/cy we need the 'data' argument from the event
                        handleMouseEnter({ ...dataPoint, cx: data.cx, cy: data.cy }, null)
                      }}
                      onMouseLeave={(data) => {
                        handleMouseLeave(dataPoint)
                      }}
                    >
                      <Cell
                        fill={color}
                        onClick={(e) => {
                          if (e && typeof e.stopPropagation === 'function') {
                            e.stopPropagation()
                          }
                          if (dataPoint.id === 0 && !showIncomePolygonData) {
                            // Clicking on target group point shows individual shapes
                            // This will also sync with people tab via showIncomeIndividualShapes action
                            rightSidebarStore.showIncomeIndividualShapes()
                          } else if (isSelected) {
                            rightSidebarStore.clearSelectedIncomePoint()
                          } else {
                            rightSidebarStore.setSelectedIncomePoint(dataPoint.id)
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                    </Scatter>
                  )
                })}
              </ScatterChart>
            </ResponsiveContainer>
            )
          : (
            <div className='income-data-sidebar__loading'>
              {rightSidebarStore && rightSidebarStore.populationData ? 'No data available for selected year' : 'Please select a target group to view data'}
            </div>
            )}
      </div>
      <div className='income-data-sidebar__chart-legend'>
        {singlePolygonData
          ? (
            // Show single polygon name when a shape is clicked from left sidebar
            <>
              <span className='income-data-sidebar__chart-legend-line'>●</span>
              <span className='income-data-sidebar__chart-legend-text'>{safeTargetGroupName}</span>
            </>
            )
          : showIncomePolygonData && polygonData.length > 0
            ? (
              // Show all polygon names when showing polygon data
                polygonData.map((polygon, index) => {
                  const color = polygon.color || polygonColors[index % polygonColors.length]
                  return (
                    <div key={polygon.id} className='income-data-sidebar__chart-legend-item'>
                      <span className='income-data-sidebar__chart-legend-line' style={{ color }}>●</span>
                      <span className='income-data-sidebar__chart-legend-text'>{polygon.name || `Polygon ${polygon.id}`}</span>
                    </div>
                  )
                })
              )
            : (
              // Show target group name
              <>
                <span className='income-data-sidebar__chart-legend-line'>●</span>
                <span className='income-data-sidebar__chart-legend-text'>{safeTargetGroupName}</span>
              </>
              )}
      </div>
    </div>
  )
}

export default EconomicStrengthSection
